'use client';

import React, { createContext, useContext, ReactNode, useCallback } from 'react';
import { useRouter } from 'next/navigation';

interface PreviewContextValue {
  isPreviewMode: boolean;
  /** Whether in edit mode (true) or navigate mode (false) - only relevant when isPreviewMode is true */
  isEditMode?: boolean;
  navigateInEditor: (slug: string) => Promise<void>;
  currentPageId?: string;
  /** Viewport width for preview mode (defaults to desktop: 1920px) */
  viewportWidth?: number;
  /** Whether preview should use desktop viewport */
  isDesktopViewport?: boolean;
}

const PreviewContext = createContext<PreviewContextValue | undefined>(undefined);

export function PreviewProvider({ 
  children, 
  isPreviewMode = false,
  isEditMode = false,
  currentPageId,
  viewportWidth = 1920,
  isDesktopViewport = true
}: { 
  children: ReactNode;
  isPreviewMode?: boolean;
  /** Whether in edit mode (blocks navigation) or navigate mode (allows navigation) */
  isEditMode?: boolean;
  currentPageId?: string;
  viewportWidth?: number;
  isDesktopViewport?: boolean;
}) {
  const router = useRouter();

  const navigateInEditor = useCallback(async (slug: string) => {
    // In edit mode, don't navigate - just block the action
    if (isEditMode) {
      return;
    }
    
    if (!isPreviewMode) return;
    
    try {
      // In navigate mode within preview, update the URL to the new page
      // Use router.replace to update slug without adding to history
      const targetSlug = slug === '' ? '' : slug;
      router.replace(`/admin/dashboard/pages/${targetSlug || 'home'}`);
    } catch (error) {
      console.error('Failed to navigate in editor:', error);
    }
  }, [isPreviewMode, isEditMode, router]);

  return (
    <PreviewContext.Provider value={{ 
      isPreviewMode, 
      isEditMode,
      navigateInEditor, 
      currentPageId,
      viewportWidth: isPreviewMode ? viewportWidth : undefined,
      isDesktopViewport: isPreviewMode ? isDesktopViewport : undefined
    }}>
      {children}
    </PreviewContext.Provider>
  );
}

// Default context value for SSR/static generation
const defaultPreviewContext: PreviewContextValue = {
  isPreviewMode: false,
  isEditMode: false,
  navigateInEditor: async (_slug: string) => { /* no-op for SSR */ },
  currentPageId: undefined,
  viewportWidth: undefined,
  isDesktopViewport: undefined,
};

export function usePreview() {
  const context = useContext(PreviewContext);
  // Return a safe default when not in a PreviewProvider (e.g., during SSR/static generation)
  return context ?? defaultPreviewContext;
}

